import React from "react";

import { snakeCase } from "lodash";
import { format } from "date-fns";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  pdf,
} from "@react-pdf/renderer";
import { saveAs } from "file-saver";

import { ReportAnswer, ReportType } from "../../../graphql/types";

import {
  TemplateQuestionFragment,
  ReportMadFragment,
  CompanyMinimalFragment,
} from "../../../graphql/fragments";

import logo from "../../../assets/mooveo_logo_variant.png";
import ReportQuestion from "./ReportQuestion";

const styles = StyleSheet.create({
  page: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#ffffff",
    marginBottom: 40,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 20,
    marginBottom: 10,
  },
  report: { paddingHorizontal: "10mm" },
  reportType: { color: "#ffffff", fontSize: "7mm", fontWeight: "black" },
  reportDate: {
    color: "#ffffff",
    fontSize: "4mm",
  },
  docusignTag: {
    margin: 12,
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 6,
    width: "33%",
    color: "white",
    fontSize: "1mm",
    border: "1pt solid #e7e5ed",
  },
  logo: { width: "30mm" },
});

type HeaderProps = {
  company: CompanyMinimalFragment;
  updatedAt: string;
  reportType: ReportType;
  t: (id: any, options?: {}) => React.ReactNode;
};

const Header: React.FC<HeaderProps> = ({
  company: { theme, visualIdentity },
  updatedAt,
  reportType,
  t,
}) => {
  return (
    <View style={[styles.header, { backgroundColor: theme.secondary }]} fixed>
      <Image style={styles.logo} src={visualIdentity || logo} />
      <Text style={styles.reportType}>{t(`reports.header.${reportType}`)}</Text>
      <Text style={styles.reportDate}>
        {format(new Date(updatedAt), t("dateFormat") as string)}
      </Text>
    </View>
  );
};

const SignatureSection: React.FC<{ tag: string }> = ({ tag }) => {
  return <Text style={styles.docusignTag}>{tag}</Text>;
};

type ReportDocumentProps = {
  entries: {
    [key: string]: {
      question: TemplateQuestionFragment;
      answers: ReportAnswer[];
    };
  };
  mad: ReportMadFragment;
  company: CompanyMinimalFragment;
  updatedAt: string;
  reportType: ReportType;
  signatories?: string[];
  pageSize: "A4" | "LETTER";
  t: (id: any, options?: {}) => React.ReactNode;
};

const ReportDocument: React.FC<ReportDocumentProps> = ({
  entries,
  mad,
  company,
  updatedAt,
  reportType,
  pageSize,
  signatories = [],
  t,
}) => {
  return (
    <Document>
      <Page size={pageSize} style={styles.page}>
        <View>
          <Header
            company={company}
            reportType={reportType}
            updatedAt={updatedAt}
            t={t}
          />
          <View style={styles.report}>
            {Object.entries(entries).map(([, { question, answers }], index) => (
              <ReportQuestion
                question={question}
                answers={answers}
                mad={mad}
                index={index}
                t={t}
                key={question.id}
              />
            ))}
            {signatories.map((signatory) => {
              return <SignatureSection key={signatory} tag={`__Docusign__${signatory}`} />;
            })}
          </View>
        </View>
      </Page>
    </Document>
  );
};

export const generatePdfDocument = async (
  entries: {
    [key: string]: {
      question: TemplateQuestionFragment;
      answers: ReportAnswer[];
    };
  },
  mad: ReportMadFragment,
  company: CompanyMinimalFragment,
  updatedAt: string,
  reportType: ReportType,
  signatories: string[],
  t: (id: any, options?: {}) => React.ReactNode,
  pageSize: "A4" | "LETTER" = "A4",
  directDownload = true
) => {
  const blob = await pdf(
    <ReportDocument
      entries={entries}
      updatedAt={updatedAt}
      mad={mad}
      company={company}
      pageSize={pageSize}
      reportType={reportType}
      signatories={signatories}
      t={t}
    />
  ).toBlob();

  const result = await new Promise<string | ArrayBuffer>((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });

  const filename = snakeCase(
    `report_${mad.talent.name.toLocaleLowerCase()}}_at_${company.name}_${format(
      new Date(updatedAt),
      t("dateFormat") as string
    )}`
  );

  if (directDownload) saveAs(result as string, filename);

  return {
    document: result as string,
    filename: filename,
  };
};
